import { useCallback, useState, type ReactNode } from 'react'
import { useNls } from '../../nls/nlsProvider'
import { useInstantSetting, type InstantSettingStatus } from './useInstantSetting'

export interface UseInstantSettingRowsResult {
  run: <T = void>(key: string, fn: () => Promise<T> | T | void) => Promise<T | undefined>
  statusFor: (key: string) => ReactNode
  activeKey: string | null
  status: InstantSettingStatus
  error: string | null
  reset: () => void
}

/**
 * useInstantSettingRows (SR5 / F2)
 * Kit helper managing instant save status per row key.
 * Integrates with `SettingsRow`'s `status` prop (`SettingsRow.tsx:12, :71-74`).
 */
export function useInstantSettingRows(): UseInstantSettingRowsResult {
  const instant = useInstantSetting()
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const { t } = useNls()

  const run = useCallback(
    async <T = void,>(key: string, fn: () => Promise<T> | T | void): Promise<T | undefined> => {
      setActiveKey(key)
      try {
        const result = await instant.run(async () => {
          const res = await fn()
          return res as T
        })
        return result
      } catch {
        // Status and error are handled by useInstantSetting; return undefined
        // so row change handlers do not create unhandled promise rejections.
        return undefined
      }
    },
    [instant],
  )

  const statusFor = useCallback(
    (key: string): ReactNode => {
      if (activeKey !== key) return null
      if (instant.status === 'saved') {
        return <span className="skRow__status--saved">✓ {t('NLSSettingsKitSaved')}</span>
      }
      if (instant.status === 'error') {
        return <span className="skRow__status--error">{instant.error || t('NLSSettingsKitSaveFailed')}</span>
      }
      if (instant.status === 'saving') {
        return <span className="skRow__status--saving">{t('NLSSettingsKitSaving')}</span>
      }
      return null
    },
    [activeKey, instant.status, instant.error, t],
  )

  const reset = useCallback(() => {
    setActiveKey(null)
    instant.reset()
  }, [instant])

  return {
    run,
    statusFor,
    activeKey,
    status: instant.status,
    error: instant.error,
    reset,
  }
}
